import { AxiosResponse } from 'axios';
import { call, fork, put, race, take, takeEvery } from 'redux-saga/effects';

import { SettingsCommentsMonthToDate } from '../domain/settingsComments';

import {
  DeleteSettingsCommentsAction,
  DeleteSettingsCommentsMonthToDateAction,
  FetchSettingsCommentsMonthToDateAction,
  PostSettingsCommentsAction,
  PostSettingsCommentsMonthToDateAction,
  PutSettingsCommentsAction,
  PutSettingsCommentsMonthToDateAction,
  SettingsCommentsMonthToDateActionCreators,
  SettingsCommentsMonthToDateActionTypes,
} from '../redux/server/settingsCommentsMonthToDate';
import { Api } from '../utils/api';
import { handleErrorSaga } from './errorSaga';

/**
 * コメント投稿
 * @param api AxiosInstance
 */
function* postSettingsCommentsSaga(
  api: Api,
  action: PostSettingsCommentsAction
) {
  const { content, date, hallCode } = action.payload.params;

  try {
    yield put(
      SettingsCommentsMonthToDateActionCreators.postSettingsCommentsRequestAction()
    );
    yield call(api.post, `/settings/comments/${hallCode}/${date}`, { content });
    yield put(
      SettingsCommentsMonthToDateActionCreators.postSettingsCommentsSuccessAction()
    );
  } catch (error: unknown) {
    yield put(
      SettingsCommentsMonthToDateActionCreators.postSettingsCommentsFailureAction(
        'コメントの投稿に失敗しました。'
      )
    );
  }
}

/** API の単月取得パス用。date は yyyy-MM-dd 形式を前提とする */
const toMonthToDatePathParams = (date: string, hallCode: string) => ({
  year: date.slice(0, 4),
  month: date.slice(5, 7),
  hallCode,
});

/**
 * POST/PUT/DELETE Worker が dispatch する Success/Failure のいずれかを待つ。
 * 失敗時に無関係な後続 SUCCESS で単月再取得が走るのを防ぐ。
 */
export function* waitForSettingsCommentsMutationResult() {
  const result: { success?: unknown; failure?: unknown } = yield race({
    success: take(
      SettingsCommentsMonthToDateActionTypes.POST_SETTINGS_COMMENTS_SUCCESS
    ),
    failure: take(
      SettingsCommentsMonthToDateActionTypes.POST_SETTINGS_COMMENTS_FAILURE
    ),
  });
  return result.success != null;
}

/**
 * コメントを投稿し、成功時に単月更新する
 */
function* postSettingsCommentsMonthToDateSaga(
  action: PostSettingsCommentsMonthToDateAction
) {
  const { params } = action.payload;

  yield put(
    SettingsCommentsMonthToDateActionCreators.postSettingsCommentsAction(params)
  );
  const succeeded: boolean = yield call(waitForSettingsCommentsMutationResult);
  if (!succeeded) {
    return;
  }
  yield put(
    SettingsCommentsMonthToDateActionCreators.fetchSettingsCommentsMonthToDateAction(
      toMonthToDatePathParams(params.date, params.hallCode)
    )
  );
}

/**
 * コメントを更新する。
 * ローディング・エラー表示は既存 POST と同じ Action を流用し、予定進捗推移の ErrorDialog と整合させる。
 */
export function* putSettingsCommentsSaga(
  api: Api,
  action: PutSettingsCommentsAction
) {
  const { id, content } = action.payload.params;

  try {
    yield put(
      SettingsCommentsMonthToDateActionCreators.postSettingsCommentsRequestAction()
    );
    yield call(api.put, `/settings/comments/${id}`, { content });
    yield put(
      SettingsCommentsMonthToDateActionCreators.postSettingsCommentsSuccessAction()
    );
  } catch (error: unknown) {
    yield put(
      SettingsCommentsMonthToDateActionCreators.postSettingsCommentsFailureAction(
        'コメントの更新に失敗しました。'
      )
    );
  }
}

/**
 * コメントを更新し、成功時に単月更新する。
 * 画面表示は settingsCommentsMonthToDate のため、dataHallDailyComments は更新しない。
 */
export function* putSettingsCommentsMonthToDateSaga(
  action: PutSettingsCommentsMonthToDateAction
) {
  const { params } = action.payload;

  yield put(
    SettingsCommentsMonthToDateActionCreators.putSettingsCommentsAction(params)
  );
  const succeeded: boolean = yield call(waitForSettingsCommentsMutationResult);
  if (!succeeded) {
    return;
  }
  yield put(
    SettingsCommentsMonthToDateActionCreators.fetchSettingsCommentsMonthToDateAction(
      toMonthToDatePathParams(params.date, params.hallCode)
    )
  );
}

/**
 * コメントを削除する。
 * PUT と同様、POST 用の Request/Success/Failure を流用する。
 */
export function* deleteSettingsCommentsSaga(
  api: Api,
  action: DeleteSettingsCommentsAction
) {
  const { id } = action.payload.params;

  try {
    yield put(
      SettingsCommentsMonthToDateActionCreators.postSettingsCommentsRequestAction()
    );
    yield call(api.delete, `/settings/comments/${id}`);
    yield put(
      SettingsCommentsMonthToDateActionCreators.postSettingsCommentsSuccessAction()
    );
  } catch (error: unknown) {
    yield put(
      SettingsCommentsMonthToDateActionCreators.postSettingsCommentsFailureAction(
        'コメントの削除に失敗しました。'
      )
    );
  }
}

/**
 * コメントを削除し、成功時に単月更新する
 */
export function* deleteSettingsCommentsMonthToDateSaga(
  action: DeleteSettingsCommentsMonthToDateAction
) {
  const { params } = action.payload;

  yield put(
    SettingsCommentsMonthToDateActionCreators.deleteSettingsCommentsAction(
      params
    )
  );
  const succeeded: boolean = yield call(waitForSettingsCommentsMutationResult);
  if (!succeeded) {
    return;
  }
  yield put(
    SettingsCommentsMonthToDateActionCreators.fetchSettingsCommentsMonthToDateAction(
      toMonthToDatePathParams(params.date, params.hallCode)
    )
  );
}

/**
 * 単月コメント取得
 * @param api AxiosInstance
 */
function* fetchSettingsCommentsMonthToDateSaga(
  api: Api,
  action: FetchSettingsCommentsMonthToDateAction
) {
  const {
    pathParams: { year, month, hallCode },
  } = action.payload;

  try {
    yield put(
      SettingsCommentsMonthToDateActionCreators.fetchSettingsCommentsMonthToDateRequestAction()
    );
    const response: AxiosResponse<SettingsCommentsMonthToDate> = yield call(
      api.get,
      `/settings/comments/${year}/${month}/${hallCode}`
    );
    yield put(
      SettingsCommentsMonthToDateActionCreators.fetchSettingsCommentsMonthToDateSuccessAction(
        response.data
      )
    );
  } catch (error: unknown) {
    yield put(
      SettingsCommentsMonthToDateActionCreators.renewSettingsCommentsMonthToDateAction()
    );
    yield fork(handleErrorSaga, error);
  }
}

function* handleFetchSettingsCommentsMonthToDateSaga(api: Api) {
  yield takeEvery(
    SettingsCommentsMonthToDateActionTypes.FETCH_SETTINGS_COMMENTS_MONTH_TO_DATE,
    fetchSettingsCommentsMonthToDateSaga,
    api
  );
}

function* handlePostSettingsCommentsMonthToDateSaga(api: Api) {
  yield takeEvery(
    SettingsCommentsMonthToDateActionTypes.POST_SETTINGS_COMMENTS,
    postSettingsCommentsSaga,
    api
  );

  yield takeEvery(
    SettingsCommentsMonthToDateActionTypes.POST_SETTINGS_COMMENTS_MONTH_TO_DATE,
    postSettingsCommentsMonthToDateSaga
  );

  yield takeEvery(
    SettingsCommentsMonthToDateActionTypes.PUT_SETTINGS_COMMENTS,
    putSettingsCommentsSaga,
    api
  );

  yield takeEvery(
    SettingsCommentsMonthToDateActionTypes.PUT_SETTINGS_COMMENTS_MONTH_TO_DATE,
    putSettingsCommentsMonthToDateSaga
  );

  yield takeEvery(
    SettingsCommentsMonthToDateActionTypes.DELETE_SETTINGS_COMMENTS,
    deleteSettingsCommentsSaga,
    api
  );

  yield takeEvery(
    SettingsCommentsMonthToDateActionTypes.DELETE_SETTINGS_COMMENTS_MONTH_TO_DATE,
    deleteSettingsCommentsMonthToDateSaga
  );
}

export function* settingsCommentsMonthToDateSagas(context: { api: Api }) {
  yield fork(handlePostSettingsCommentsMonthToDateSaga, context.api);
  yield fork(handleFetchSettingsCommentsMonthToDateSaga, context.api);
}
